import { useState, useEffect, useRef } from "react";
import api, { uploadFile, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import FileUpload from "@/components/FileUpload";
import LiquorPhotoEditor from "@/components/LiquorPhotoEditor";
import { ocrToProductPatch } from "@/lib/ocrMap";
import { ORIGINS, APPLICATIONS, TEA_CATEGORIES, SAMPLE_TYPES, CERTIFICATIONS } from "@/lib/constants";
import { ScanText, Loader2, CheckCircle2, AlertTriangle, Wand2, Crop } from "lucide-react";

const MATCH_META = {
  green: { label: "Matching", cls: "bg-emerald-100 text-emerald-800 border-emerald-300", Icon: CheckCircle2 },
  amber: { label: "Info missing", cls: "bg-amber-100 text-amber-800 border-amber-300", Icon: AlertTriangle },
  red: { label: "Possible mismatch", cls: "bg-red-100 text-red-800 border-red-300", Icon: AlertTriangle },
};

// Report placeholder on the catalogue page is 640 × 355 pt → 1.8:1 landscape (e.g. 1800 × 1000 px)
const REPORT_ASPECT = 1.8;
const REPORT_ASPECTS = [
  { label: "Catalogue fit 1.8:1", value: REPORT_ASPECT },
  { label: "16:9", value: 16 / 9 },
  { label: "4:3", value: 4 / 3 },
];


const OCR_FIELDS = [
  ["garden", "Manufacturer or Supplier"], ["brand_group", "Garden or Factory"], ["grade", "Grade"],
  ["invoice_no", "Invoice No."], ["lot_no", "Lot No."], ["no_of_bags", "No. of Bags"], ["net_qty", "Net Weight"],
  ["report_date", "Report date"], ["tea_category", "Tea category"], ["certification", "Certification"],
  ["liquor_color", "Liquor colour"], ["body", "Body"], ["strength", "Strength"], ["briskness", "Briskness"],
  ["taste_aroma", "Taste & aroma"], ["milk_suitability", "Milk suitability"],
];

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold text-[#14513D]">{label}</Label>
      {children}
    </div>
  );
}

export default function ProductEditor({ open, onOpenChange, product, onSave, onSaveToLibrary }) {
  const [p, setP] = useState(product && Object.keys(product).length ? product : { gst_percent: "5" });
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocr, setOcr] = useState(null);
  const [editPhoto, setEditPhoto] = useState(false);
  const [editReport, setEditReport] = useState(false);
  const [confirmBar, setConfirmBar] = useState(false);
  const [match, setMatch] = useState(
    product?.match_status ? { status: product.match_status, reasons: product.match_reasons || [] } : null
  );
  const [fieldMatch, setFieldMatch] = useState({});
  const [ocrData, setOcrData] = useState(null);
  const [lotClash, setLotClash] = useState(null);
  const libraryContext = !onSaveToLibrary;

  // live duplicate-lot check against the product library (debounced)
  useEffect(() => {
    const lot = (p.lot_no || "").trim();
    if (!lot) { setLotClash(null); return; }
    const exclude = libraryContext ? p.id : p.library_id;
    const t = setTimeout(() => {
      api.get("/products/check-lot", { params: { lot_no: lot, exclude } })
        .then((r) => setLotClash(r.data.exists ? r.data : null))
        .catch(() => setLotClash(null));
    }, 350);
    return () => clearTimeout(t);
  }, [p.lot_no, p.id, p.library_id, libraryContext]);

  const set = (k, v) => {
    setP((x) => ({ ...x, [k]: v }));
    setFieldMatch((fm) => {
      if (!fm[k]) return fm;
      const n = { ...fm };
      delete n[k];
      return n;
    });
  };
  const toggleApp = (val) => {
    const cur = p.best_application || [];
    set("best_application", cur.includes(val) ? cur.filter((a) => a !== val) : [...cur, val]);
  };

  const applyOcr = (data, showBar = true) => {
    const o = data.ocr || {};
    setP((x) => ({
      ...x,
      ...ocrToProductPatch(o, x),
      match_status: data.match?.status,
      match_reasons: data.match?.reasons,
    }));
    const fm = {};
    Object.entries(data.field_match || {}).forEach(([k, v]) => {
      fm[k === "no_of_bags" ? "chest_bag" : k] = v;
    });
    setFieldMatch(fm);
    setOcr(o);
    setOcrData(data);
    setMatch(data.match);
    if (showBar) setConfirmBar(true);
  };

  const acceptAll = () => {
    if (ocrData) applyOcr(ocrData, false);
    setConfirmBar(false);
    toast.success("Applied all report values");
  };

  const doAnalyze = async (reportId, liquorId) => {
    if (!reportId) return;
    setOcrBusy(true);
    try {
      const { data } = await api.post("/ocr/analyze", {
        report_file_id: reportId,
        liquor_image_file_id: liquorId || null,
        entered: { garden: p.garden, grade: p.grade, invoice_no: p.invoice_no, lot_no: p.lot_no, report_date: p.report_date, brand_group: p.brand_group },
      });
      applyOcr(data);
      const mismatches = Object.values(data.field_match || {}).filter((v) => v === "mismatch").length;
      toast.success(mismatches
        ? `Report read — fields auto-filled. ${mismatches} field(s) don't match the sample photo (in red).`
        : "Report read — fields auto-filled from the report.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "OCR failed");
    } finally {
      setOcrBusy(false);
    }
  };

  const runOcr = () => {
    if (!p.report_file_id) return toast.error("Upload a tasting report first");
    doAnalyze(p.report_file_id, p.liquor_image_file_id);
  };

  // Auto-run OCR when a NEW report is uploaded (runs post-commit so liquor id is fresh).
  const lastReportRef = useRef(product?.report_file_id || null);
  useEffect(() => {
    const rid = p.report_file_id;
    if (rid && rid !== lastReportRef.current) {
      lastReportRef.current = rid;
      doAnalyze(rid, p.liquor_image_file_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.report_file_id]);

  const applyField = (k) => ocr?.[k] && set(k, ocr[k]);
  const applyAll = () => { if (ocrData) applyOcr(ocrData); };
  const applyEditedPhoto = async (file) => {
    try {
      const data = await uploadFile(file, "liquor");
      setP((x) => ({
        ...x,
        liquor_image_file_id: data.id,
        liquor_image_url: data.url,
        liquor_image_width: data.width,
        liquor_image_height: data.height,
        liquor_image_content_type: data.content_type,
      }));
      toast.success("Photo updated");
    } catch (e) {
      toast.error("Could not save edited photo");
    }
  };

  const applyEditedReport = async (file) => {
    try {
      const data = await uploadFile(file, "report");
      setP((x) => ({
        ...x,
        report_file_id: data.id,
        report_url: data.url,
        report_width: data.width,
        report_height: data.height,
        report_content_type: data.content_type,
        report_filename: data.original_filename,
      }));
      toast.success("Report image updated");
    } catch (e) {
      toast.error("Could not save edited report");
    }
  };

  const save = async () => {
    if (lotClash && libraryContext) return toast.error(lotClash.message);
    const ok = await onSave(p);
    if (ok !== false) onOpenChange(false);
  };

  const textInput = (k, ph) => {
    const st = fieldMatch[k];
    const cls =
      st === "mismatch" ? "border-red-500 ring-1 ring-red-400 bg-red-50" :
      st === "only_report" || st === "only_photo" ? "border-amber-400 bg-amber-50" : "";
    return (
      <div>
        <Input value={p[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder={ph} data-testid={`product-${k}`} className={cls} />
        {st === "mismatch" && <div className="text-[11px] text-red-600 mt-0.5" data-testid={`mismatch-${k}`}>Doesn't match the sample photo — verify &amp; edit.</div>}
        {(st === "only_report" || st === "only_photo") && <div className="text-[11px] text-amber-600 mt-0.5">Only found in {st === "only_report" ? "the report" : "the photo"} — please confirm.</div>}
      </div>
    );
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#0B3B2E] flex items-center gap-3">
            Product {p.position ? `#${p.position}` : ""} · {p.brand_group || p.garden || p.name || "New Tea"}
            {match && (
              <Badge variant="outline" className={MATCH_META[match.status]?.cls} data-testid="match-badge">
                {MATCH_META[match.status]?.label}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {confirmBar && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#C9A34A]/50 bg-[#FAF7F0] px-4 py-2.5" data-testid="confirm-bar">
            <div className="text-sm text-[#14513D]">Fields were auto-filled from the report. Accept all report values, or keep your manual edits?</div>
            <div className="flex gap-2 shrink-0">
              <Button size="sm" variant="outline" onClick={() => setConfirmBar(false)} data-testid="keep-edits">Keep my edits</Button>
              <Button size="sm" onClick={acceptAll} data-testid="accept-all" className="bg-[#0B3B2E] hover:bg-[#14513D]">Accept all report values</Button>
            </div>
          </div>
        )}

        <Tabs defaultValue="identity">
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="identity" data-testid="tab-identity">Identity</TabsTrigger>
            <TabsTrigger value="commercial" data-testid="tab-commercial">Commercial</TabsTrigger>
            <TabsTrigger value="sensory" data-testid="tab-sensory">Sensory</TabsTrigger>
            <TabsTrigger value="media" data-testid="tab-media">Media</TabsTrigger>
          </TabsList>

          <TabsContent value="identity" className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <Field label="Product name">{textInput("name", "e.g. Assam CTC Gold")}</Field>
            <Field label="Manufacturer or Supplier">{textInput("garden", "e.g. Doomni Tea Estate")}</Field>
            <Field label="Garden or Factory">{textInput("brand_group", "Prime Gold")}</Field>
            <Field label="Origin">
              <Select value={p.origin || ""} onValueChange={(v) => set("origin", v)}>
                <SelectTrigger data-testid="product-origin"><SelectValue placeholder="Select origin" /></SelectTrigger>
                <SelectContent>{ORIGINS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Grade">{textInput("grade", "e.g. BP, PD, FTGFOP")}</Field>
            <Field label="Tea category">
              <Select value={p.tea_category || ""} onValueChange={(v) => set("tea_category", v)}>
                <SelectTrigger data-testid="product-tea_category"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>{TEA_CATEGORIES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Invoice number">{textInput("invoice_no", "INV-…")}</Field>
            <Field label="Lot number">
              {textInput("lot_no", "LOT-…")}
              {lotClash && (
                <div className="text-[11px] text-red-600 mt-0.5 font-medium" data-testid="lot-duplicate-warning">
                  {lotClash.message}
                </div>
              )}
            </Field>
            <Field label="Chest or bag details">{textInput("chest_bag", "e.g. 40 chests")}</Field>
            <Field label="Total net quantity">{textInput("net_qty", "e.g. 1200 kg")}</Field>
            <Field label="Sample type">
              <Select value={p.sample_type || ""} onValueChange={(v) => set("sample_type", v)}>
                <SelectTrigger data-testid="product-sample_type"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>{SAMPLE_TYPES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Certification">
              <Select value={p.certification || ""} onValueChange={(v) => set("certification", v)}>
                <SelectTrigger data-testid="product-certification"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{CERTIFICATIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </TabsContent>

          <TabsContent value="commercial" className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <Field label="Report date">
              <Input type="date" value={p.report_date || ""} onChange={(e) => set("report_date", e.target.value)} data-testid="product-report_date" />
            </Field>
            <Field label="Price per kg — excl. GST (optional)">{textInput("price_per_kg", "e.g. 285")}</Field>
            <Field label="GST %">{textInput("gst_percent", "e.g. 5")}</Field>
            <Field label="Available quantity (optional)">{textInput("available_qty", "e.g. 800 kg")}</Field>
            <Field label="Validity date (optional)">
              <Input type="date" value={p.validity_date || ""} onChange={(e) => set("validity_date", e.target.value)} data-testid="product-validity_date" />
            </Field>
            <div className="sm:col-span-2 flex items-center gap-3 rounded-md border border-[#C9A34A]/30 bg-[#FAF7F0] px-3 py-2">
              <Switch checked={(p.stock_status || "in_stock") !== "out_of_stock"} onCheckedChange={(v) => set("stock_status", v ? "in_stock" : "out_of_stock")} data-testid="product-stock" />
              <span className="text-sm font-medium text-[#14513D]">{(p.stock_status || "in_stock") !== "out_of_stock" ? "In stock" : "Out of stock"}</span>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs font-semibold text-[#14513D]">Best commercial application (select multiple)</Label>
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                {APPLICATIONS.map((a) => (
                  <label key={a.value} className="flex items-center gap-2 rounded-md border border-[#C9A34A]/30 px-2 py-1.5 cursor-pointer hover:bg-[#C9A34A]/5 text-sm">
                    <Checkbox
                      checked={(p.best_application || []).includes(a.value)}
                      onCheckedChange={() => toggleApp(a.value)}
                      data-testid={`app-${a.value}`}
                    />
                    {a.label}
                  </label>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="sensory" className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <Field label="Liquor colour and brightness">{textInput("liquor_color", "e.g. Bright coppery, brisk")}</Field>
            <Field label="Body">{textInput("body", "e.g. Full")}</Field>
            <Field label="Strength">{textInput("strength", "e.g. Strong")}</Field>
            <Field label="Briskness">{textInput("briskness", "e.g. High")}</Field>
            <Field label="Milk suitability">{textInput("milk_suitability", "e.g. Excellent with milk")}</Field>
            <Field label="Taste and aroma">
              <Textarea value={p.taste_aroma || ""} onChange={(e) => set("taste_aroma", e.target.value)} placeholder="Malty, brisk finish" data-testid="product-taste_aroma" />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Short customer-facing selling note">
                <Textarea value={p.selling_note || ""} onChange={(e) => set("selling_note", e.target.value)} placeholder="A robust, malty CTC ideal for strong milk tea." data-testid="product-selling_note" />
              </Field>
            </div>
          </TabsContent>

          <TabsContent value="media" className="mt-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <FileUpload
                  label="Liquor tasting image"
                  accept=".jpg,.jpeg,.png,.webp,.heic,.heif"
                  purpose="liquor"
                  testid="liquor-upload"
                  minWidth={800}
                  value={p.liquor_image_file_id ? { id: p.liquor_image_file_id, url: p.liquor_image_url, width: p.liquor_image_width, height: p.liquor_image_height, content_type: p.liquor_image_content_type } : null}
                  onChange={(f) =>
                    setP((x) => ({
                      ...x,
                      liquor_image_file_id: f?.id || null,
                      liquor_image_url: f?.url || null,
                      liquor_image_width: f?.width || null,
                      liquor_image_height: f?.height || null,
                      liquor_image_content_type: f?.content_type || null,
                    }))
                  }
                />
                {p.liquor_image_file_id && p.liquor_image_content_type !== "application/pdf" && (
                  <Button variant="outline" size="sm" onClick={() => setEditPhoto(true)} data-testid="liquor-edit" className="mt-2 w-full gap-1">
                    <Crop className="h-4 w-4" /> Crop &amp; exposure
                  </Button>
                )}
              </div>
              <div>
                <FileUpload
                  label="Tea tasting report"
                  accept=".jpg,.jpeg,.png,.pdf"
                  purpose="report"
                  testid="report-upload"
                  minWidth={900}
                  aspectHint={REPORT_ASPECT}
                  aspectLabel="landscape 1.8:1 (1800 × 1000 px)"
                  aspectAdvice="A different ratio is never cropped, but it will leave gaps at the sides of the report placeholder. Export the report at 1800 × 1000 px (16:9 also fits) for an edge-to-edge fit."
                  value={p.report_file_id ? { id: p.report_file_id, url: p.report_url, width: p.report_width, height: p.report_height, content_type: p.report_content_type, original_filename: p.report_filename } : null}
                  onChange={(f) =>
                    setP((x) => ({
                      ...x,
                      report_file_id: f?.id || null,
                      report_url: f?.url || null,
                      report_width: f?.width || null,
                      report_height: f?.height || null,
                      report_content_type: f?.content_type || null,
                      report_filename: f?.original_filename || null,
                    }))
                  }
                />
                {p.report_file_id && p.report_content_type !== "application/pdf" && (
                  <Button variant="outline" size="sm" onClick={() => setEditReport(true)} data-testid="report-edit" className="mt-2 w-full gap-1">
                    <Crop className="h-4 w-4" /> Crop, rotate &amp; exposure
                  </Button>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-[#C9A34A]/30 bg-[#FAF7F0] p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <ScanText className="h-5 w-5 text-[#0B3B2E]" />
                  <div>
                    <div className="font-semibold text-[#0B3B2E]">AI report reader &amp; match check</div>
                    <div className="text-xs text-[#14513D]/70">Runs automatically when you upload a report. Fields in red don't match the sample photo — edit to resolve. Internal only.</div>
                  </div>
                </div>
                <Button onClick={runOcr} disabled={ocrBusy || !p.report_file_id} data-testid="ocr-run" className="bg-[#0B3B2E] hover:bg-[#14513D] gap-2">
                  {ocrBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanText className="h-4 w-4" />}
                  {ocrBusy ? "Reading…" : "Re-read report"}
                </Button>
              </div>

              {match && (
                <div className={`mt-3 rounded-lg border px-3 py-2 ${MATCH_META[match.status]?.cls}`} data-testid="match-panel">
                  <div className="flex items-center gap-2 font-semibold">
                    {(() => { const I = MATCH_META[match.status]?.Icon; return I ? <I className="h-4 w-4" /> : null; })()}
                    {MATCH_META[match.status]?.label}
                  </div>
                  <ul className="mt-1 list-disc list-inside text-xs">
                    {(match.reasons || []).map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                </div>
              )}

              {ocr && (
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-semibold text-[#14513D]">Extracted from report — confirm before applying</div>
                    <Button size="sm" variant="outline" onClick={applyAll} data-testid="ocr-apply-all" className="gap-1"><Wand2 className="h-3.5 w-3.5" /> Apply all</Button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {OCR_FIELDS.filter(([k]) => ocr[k]).map(([k, label]) => (
                      <div key={k} className="flex items-center gap-2 rounded-md bg-white/70 border border-[#C9A34A]/20 px-2 py-1.5">
                        <div className="min-w-0 flex-1">
                          <div className="text-[10px] uppercase tracking-wide text-[#C9A34A]">{label}</div>
                          <div className="text-sm text-[#17251F] truncate">{ocr[k]}</div>
                        </div>
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => applyField(k)} data-testid={`ocr-apply-${k}`}>Apply</Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="gap-2 sm:gap-2">
          {onSaveToLibrary && (
            <Button variant="outline" onClick={() => onSaveToLibrary(p)} disabled={!!lotClash} title={lotClash?.message} data-testid="product-save-library" className="mr-auto">
              Save to Library
            </Button>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="product-cancel">Cancel</Button>
          <Button onClick={save} disabled={!!lotClash && libraryContext} title={libraryContext ? lotClash?.message : undefined} data-testid="product-save" className="bg-[#0B3B2E] hover:bg-[#14513D]">Save Product</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {editPhoto && p.liquor_image_url && (
      <LiquorPhotoEditor
        open={editPhoto}
        onOpenChange={setEditPhoto}
        imageUrl={fileUrl(p.liquor_image_url)}
        onApply={applyEditedPhoto}
      />
    )}
    {editReport && p.report_url && p.report_content_type !== "application/pdf" && (
      <LiquorPhotoEditor
        open={editReport}
        onOpenChange={setEditReport}
        imageUrl={fileUrl(p.report_url)}
        onApply={applyEditedReport}
        title="Crop, rotate & exposure — report"
        defaultAspect={REPORT_ASPECT}
        aspects={REPORT_ASPECTS}
      />
    )}
    </>
  );
}
